<template>
    <div class="tc-add">
        <!-- Body: Editor + File Upload -->
        <div class="tc-add__body">
            <base-editor :key="reload" v-model="comment.comment" :errorMessage="formErrors.comment" />

            <CustomFileUpload ref="FileUploading" @has-files="hasFiles" @files-uploaded="saveFileIds" />
        </div>

        <!-- Footer: Hint + Save -->
        <div class="tc-add__footer">
            <span class="tc-add__file-hint">
                <i class="pi pi-paperclip"></i>
                Individual file size limit: 4MB &middot; Total max: 10MB
            </span>
            <Button
                label="Save Comment"
                icon="pi pi-send"
                class="p-button-success"
                :loading="loading"
                :disabled="loading"
                @click="submit"
            />
        </div>

        <loader-dialog v-if="loading" />
    </div>
</template>

<script setup>
import { CustomFileUpload } from '../../../../../modules/custom-inputs';
import LoaderDialog from '../../../../../components/LoaderDialog.vue';

import { ref, defineProps, onUnmounted } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { storeToRefs } from 'pinia';
import { commentStore, formErrorStore } from '../../../../../store/global-stores';
import { CommentSchema, handleErrors } from '../../../../../utilities/schemas';
import { useConfirmRemove } from '../../../../../composables/ConfirmComposable';
import { showToast } from '../../../../../utilities/toast-handler';

const props = defineProps({
    taskId: {
        type: [Number, String],
    },
});

const { comment } = storeToRefs(commentStore);
const { formErrors } = storeToRefs(formErrorStore);
const { confirmContinue } = useConfirmRemove();

// states
const FileUploading = ref(null);
const filesExist = ref(false);
const loading = ref(false);
const reload = ref(0)

// check if any comment have been typed and haven't been posted yet
onBeforeRouteLeave(async (to, from, next) => {
    if (comment.value?.comment) {
        const dialogText = 'There is an unsaved comment.';
        const nextStep = await confirmContinue(dialogText);
        if (!nextStep) {
            // add a form error to show the error
            formErrors.value = { comment: "The comment hasn't been saved" };
            next(false);
        }
    } else if (filesExist.value) {
        const dialogText = 'There are some unsaved attachments.';
        const nextStep = await confirmContinue(dialogText);
        if (!nextStep) {
            next(false);
        }
    }
    next();
});

onUnmounted(() => {
    formErrorStore.clearErrors();
    if (comment.comment) {
        // clear comment
        commentStore.initComment();
    }
});

// keeps track of file attachments in the comment
const hasFiles = (event) => {
    filesExist.value = event.hasFile;
};

// when files exists and they get uploaded, this will be triggered
const saveFileIds = (event) => {
    let attachments = [];
    event.files.forEach((item) => {
        attachments.push(item.id);
    });
    // now save it into the comment attachments
    comment.value.attachments = attachments;
    addComment();
};

const submit = async () => {
    await validateAndSubmit(CommentSchema);

    reload.value = reload.value + 1
};

const addComment = async () => {
    await commentStore.addTaskComment(props.taskId);
    // Clear file flags and reset file upload component
    filesExist.value = false;
    if (FileUploading.value) {
        FileUploading.value.clearFiles?.();
    }
    loading.value = false;
    reload.value = reload.value + 1
};

const validateAndSubmit = async (schema) => {
    try {
        loading.value = true;
        comment.value.type = '';
        await schema.validate(comment.value, { abortEarly: false });
        formErrorStore.clearErrors();
        //  check if there are attachments
        // if there is then upload them first
        if (filesExist.value) {
            await FileUploading.value.uploadFiles();
        } else {
            await addComment();
        }
    } catch (error) {
        if (error.inner && typeof error.inner === 'object') {
            const errors = handleErrors(error);
            formErrorStore.addErrors(errors);
            showToast({ summary: error+' | Invalid Inputs' }, 'Input data invalid');
        }
        loading.value = false;
    }
};
</script>
